// Utilitários de formatação de valores e datas

/**
 * Formata um número como moeda brasileira (BRL)
 */
export const formatCurrency = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL'
  }).format(value);
};

/**
 * Formata um número como moeda brasileira sem símbolo
 */
export const formatCurrencyCompact = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value);
};

/**
 * Formata uma data para o padrão brasileiro (dd/mm/yyyy)
 */
export const formatDate = (date: string | Date): string => {
  if (!date) return '-';
  return new Date(date).toLocaleDateString('pt-BR');
};

/**
 * Formata uma data para o padrão brasileiro com hora (dd/mm/yyyy HH:mm)
 */
export const formatDateTime = (date: string | Date): string => {
  if (!date) return '-';
  return new Date(date).toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Formata um número como porcentagem
 */
export const formatPercentage = (value: number, decimals: number = 2): string => {
  return `${value.toFixed(decimals)}%`;
};

/**
 * Formata número grande de forma compacta (ex: 1.5M, 2.3K)
 */
export const formatCompactNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR', {
    notation: 'compact',
    maximumFractionDigits: 1
  }).format(value);
};

/**
 * Formata um número com separadores de milhar (padrão brasileiro)
 */
export const formatNumber = (value: number): string => {
  return new Intl.NumberFormat('pt-BR').format(value);
};

/**
 * Formata string de input como número decimal pt-BR (separador de milhar e vírgula decimal).
 * Aceita apenas dígitos; retorna string formatada como "1.300.000,00".
 * @param raw - Valor bruto digitado (pode conter pontos, vírgulas, letras)
 * @param decimals - Quantidade de casas decimais (default 2; use 0 para inteiros)
 * @param maxDigits - Limite de dígitos totais (inteiros + decimais)
 */
export const formatBRDecimalInput = (raw: string, decimals: number = 2, maxDigits?: number): string => {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  // Limitar quantidade total de dígitos (inteiros + decimais)
  if (maxDigits !== undefined) {
    digits = digits.slice(0, maxDigits);
  }

  const padded = digits.padStart(decimals + 1, '0');
  const intPart = padded.slice(0, padded.length - decimals).replace(/^0+(?=\d)/, '') || '0';
  const decPart = decimals > 0 ? padded.slice(padded.length - decimals) : '';

  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return decimals > 0 ? `${formattedInt},${decPart}` : formattedInt;
};

/** Alias monetário — mesma lógica, nome semântico para campos de R$ */
export const formatBRLInput = formatBRDecimalInput;

/**
 * Converte string decimal pt-BR ("1.300.000,00") para string numérica ("1300000.00").
 * Retorna string vazia se input estiver vazio.
 */
export const parseBRDecimalInput = (formatted: string): string => {
  if (!formatted) return '';
  const cleaned = formatted.replace(/\./g, '').replace(',', '.');
  const num = parseFloat(cleaned);
  return isNaN(num) ? '' : num.toString();
};

/** Alias monetário — mesma lógica, nome semântico para campos de R$ */
export const parseBRLInput = parseBRDecimalInput;

/**
 * Formata string de input como número inteiro brasileiro (com separador de milhar).
 * Ex: "1300" → "1.300"
 */
export const formatBRIntegerInput = (raw: string, maxDigits?: number): string => {
  let digits = raw.replace(/\D/g, '');
  if (!digits) return '';

  if (maxDigits !== undefined) {
    digits = digits.slice(0, maxDigits);
  }

  const num = digits.replace(/^0+(?=\d)/, '') || '0';
  return num.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
};

/**
 * Converte string de inteiro formatado ("1.300") para string numérica ("1300").
 */
export const parseBRIntegerInput = (formatted: string): string => {
  if (!formatted) return '';
  return formatted.replace(/\./g, '');
};

/**
 * Sanitiza lista de valores numéricos separados por vírgula.
 * Remove espaços excedentes, entradas vazias, vírgulas duplicadas.
 * Retorna string normalizada como "45, 60, 80" ou string vazia.
 */
export const sanitizeCommaSeparatedNumbers = (raw: string): string => {
  if (!raw) return '';
  return raw
    .split(',')
    .map(s => s.trim())
    .filter(s => s !== '' && /^\d+(\.\d+)?$/.test(s))
    .join(', ');
};
